import React from 'react';
import { ExternalLink, Combine, Scissors, Minimize2, FileSignature, ShieldCheck } from 'lucide-react';
import { PORTAL_SHELL } from '../portalTheme';
import { getStirlingPdfUrl } from '../../../constants';
import { isAllowedStirlingEmbedSrc } from '../portalStirlingOrigins';

/** Typische PDF-Aufgaben (nur Beschreibung — Werkzeug bleibt Stirling, kein eigener Editor). */
const PDF_HIGHLIGHTS: Array<{ icon: typeof Combine; label: string; hint: string }> = [
  { icon: Combine, label: 'Zusammenführen', hint: 'Mehrere PDFs zu einem Dokument verbinden.' },
  { icon: Scissors, label: 'Teilen', hint: 'Seiten herauslösen oder Dokument aufteilen.' },
  { icon: Minimize2, label: 'Komprimieren', hint: 'Dateigröße für E-Mail/Upload reduzieren.' },
  { icon: FileSignature, label: 'Signieren', hint: 'Unterschrift oder Stempel einfügen.' },
];

function PdfToolsFallbackLink({ href }: { href: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      data-testid="portal-pdf-tools-fallback-tab"
      className="inline-flex shrink-0 items-center gap-1.5 text-xs text-slate-500 hover:text-slate-300"
    >
      <ExternalLink className="w-3 h-3" aria-hidden />
      Falls die Fläche hier leer bleibt: in neuem Tab öffnen
    </a>
  );
}

/**
 * PDF-Werkzeuge (Stirling-PDF) — zusätzlich zu OnlyOffice (DOCX/Office).
 * Klasse STIRLING_PORTAL_EMBED_NARROW + PORTAL_PANE_FIT / DIKTAT_PANE_SCROLL_OR_GROW:
 * iframe nur pdf-labor auf Labor/Thomas; volle Portal-Pane (`flex-1 min-h-0`, kein Mini-iframe).
 * Fallback-Tab sekundär. Kein eigener PDF-Editor, keine Ausgabe-CI/Stempel-Shell.
 */
export const PdfToolsView: React.FC = () => {
  const baseUrl = getStirlingPdfUrl();
  const available = Boolean(baseUrl);
  const embedOk = isAllowedStirlingEmbedSrc(baseUrl);
  const fillPane = available && embedOk;

  return (
    <div
      className={
        fillPane
          ? '@container w-full min-w-0 max-w-none h-full min-h-0 flex flex-1 flex-col overflow-hidden animate-fade-in'
          : 'w-full min-w-0 max-w-none min-h-0 flex flex-1 flex-col overflow-y-auto custom-scrollbar animate-fade-in space-y-4'
      }
      data-testid="portal-pdf-tools-pane"
      data-layout="pane-fit"
      data-pdf-tools-pane="scroll-or-grow"
    >
      <header className="shrink-0 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 pb-2">
        <div className="min-w-0">
          <h1 className="text-base md:text-lg font-semibold text-white tracking-tight">PDF-Werkzeuge</h1>
          <p className={`${PORTAL_SHELL.textMuted} text-xs mt-0.5`}>
            Zusammenführen, teilen, komprimieren, signieren
          </p>
        </div>
        {fillPane ? <PdfToolsFallbackLink href={baseUrl} /> : null}
      </header>

      {fillPane ? (
        <section
          className={`${PORTAL_SHELL.ingestBar} flex flex-1 min-h-0 flex-col overflow-hidden`}
          data-testid="portal-pdf-tools-embed-wrap"
        >
          <iframe
            src={baseUrl}
            title="PDF-Werkzeuge"
            data-testid="portal-pdf-tools-embed"
            className="w-full h-full min-h-[12rem] flex-1 min-w-0 border-0 bg-[#0b0d10]"
            referrerPolicy="strict-origin-when-cross-origin"
          />
        </section>
      ) : available ? (
        <section className={`${PORTAL_SHELL.ingestBar} p-5 space-y-4`} data-testid="portal-pdf-tools-embed-blocked">
          <p className="text-amber-200/90 text-sm leading-relaxed">
            Die PDF-Werkzeuge lassen sich hier nicht einbetten. Sie können sie in einem neuen Tab öffnen.
          </p>
          <a
            href={baseUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="portal-pdf-tools-fallback-tab"
            className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2.5 rounded-lg bg-sky-600 hover:bg-sky-500 text-white text-sm font-medium transition-colors"
          >
            <ExternalLink className="w-4 h-4" aria-hidden />
            PDF-Werkzeuge in neuem Tab öffnen
          </a>
        </section>
      ) : (
        <section className={`${PORTAL_SHELL.ingestBar} p-5 space-y-3`} data-testid="portal-pdf-tools-honest-block">
          <p className="text-slate-400 text-sm leading-relaxed">
            Die PDF-Werkzeuge sind für diese Umgebung noch nicht freigeschaltet.
          </p>
        </section>
      )}

      {!fillPane ? (
        <section className="space-y-3 shrink-0">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Typische Aufgaben</h2>
          <div className="grid gap-2 text-sm text-slate-400 sm:grid-cols-2">
            {PDF_HIGHLIGHTS.map(({ icon: Icon, label, hint }) => (
              <p key={label} className="flex items-start gap-2">
                <Icon className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" aria-hidden />
                <span>
                  <span className="text-slate-300 font-medium">{label}</span> — {hint}
                </span>
              </p>
            ))}
          </div>
          <div className="flex items-start gap-2 text-xs text-slate-500 leading-relaxed">
            <ShieldCheck className="w-4 h-4 text-emerald-500/80 flex-shrink-0 mt-0.5" aria-hidden />
            Auf eigenem Server in der EU · zusätzlich zum Editor
          </div>
        </section>
      ) : null}
    </div>
  );
};
